import React, { useMemo, useState } from 'react';
import { Alert, Image, Platform, StyleSheet, View } from 'react-native';
import { Button, Text } from 'react-native-paper';
import { useTranslation } from 'react-i18next';

import { Palette, radius, spacing, useThemePalette } from '../../theme';
import { recordError } from '../../lib/analytics';

interface Props {
  value: string; // data URI or ''
  onChange: (next: string) => void;
}

const PhotoPicker: React.FC<Props> = ({ value, onChange }) => {
  const { t } = useTranslation();
  const palette = useThemePalette();
  const styles = useMemo(() => makeStyles(palette), [palette]);
  const [busy, setBusy] = useState(false);

  const pick = async () => {
    if (busy) return;
    setBusy(true);
    try {
      // Lazy-require so the web bundle doesn't try to resolve the native
      // module. The Resume Builder tab is mobile-only anyway, but this
      // keeps the bundle safe to render the placeholder on web.
      // eslint-disable-next-line @typescript-eslint/no-require-imports
      const ImagePicker = require('expo-image-picker') as typeof import('expo-image-picker');
      const perm = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (!perm.granted) {
        Alert.alert(
          t('resume.photo.permissionTitle'),
          t('resume.photo.permissionMessage')
        );
        return;
      }
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        aspect: [1, 1],
        quality: 0.5,
        base64: true,
      });
      if (result.canceled) return;
      const asset = result.assets[0];
      if (!asset?.base64) return;
      // Most images coming from expo-image-picker are JPEG; PNG passes
      // through if the source was PNG. The MIME hint just helps img tags
      // render the data URI; for DOCX we strip it back off.
      const mime =
        asset.mimeType ||
        (asset.uri.toLowerCase().endsWith('.png') ? 'image/png' : 'image/jpeg');
      onChange(`data:${mime};base64,${asset.base64}`);
    } catch (err) {
      recordError(
        err instanceof Error ? err : new Error(String(err)),
        'resume.photo.pick'
      );
    } finally {
      setBusy(false);
    }
  };

  const clear = () => onChange('');

  return (
    <View style={styles.row}>
      <View style={[styles.thumbWrap, !value && styles.thumbEmpty]}>
        {value ? (
          <Image source={{ uri: value }} style={styles.thumb} />
        ) : (
          <Text style={styles.thumbPlaceholder}>
            {t('resume.photo.placeholder')}
          </Text>
        )}
      </View>
      <View style={styles.controls}>
        <Button
          mode="outlined"
          icon="image-outline"
          onPress={pick}
          loading={busy}
          disabled={busy || Platform.OS === 'web'}
          textColor={palette.textPrimary}
          style={styles.btn}
        >
          {value ? t('resume.photo.change') : t('resume.photo.choose')}
        </Button>
        {value !== '' && (
          <Button
            mode="text"
            icon="delete-outline"
            onPress={clear}
            textColor={palette.danger}
            style={styles.btn}
          >
            {t('resume.photo.remove')}
          </Button>
        )}
      </View>
    </View>
  );
};

const makeStyles = (p: Palette) =>
  StyleSheet.create({
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.md,
      paddingVertical: spacing.sm,
    },
    thumbWrap: {
      width: 88,
      height: 88,
      borderRadius: radius.pill,
      overflow: 'hidden',
      backgroundColor: p.surfaceMuted,
      borderWidth: 1,
      borderColor: p.border,
      alignItems: 'center',
      justifyContent: 'center',
    },
    thumbEmpty: { backgroundColor: p.surfaceMuted },
    thumb: { width: '100%', height: '100%' },
    thumbPlaceholder: {
      color: p.textMuted,
      fontSize: 11,
      textAlign: 'center',
      paddingHorizontal: spacing.xs,
    },
    controls: { flex: 1, gap: spacing.xs },
    btn: { borderRadius: radius.pill, alignSelf: 'flex-start' },
  });

export default PhotoPicker;
